import { useCallback, useEffect, useRef, useState } from "react";
import { StackedTokenIcons } from "~/components/stacked-token-icons";
import { getOnboardingV1RecommendationReturnDisplay } from "~/features/start/data/onboarding-v1-recommendation-return";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  NativeSelect,
  NativeSelectOption,
} from "~/components/ui/native-select";
import {
  hasOnboardingV1ProvisioningState,
  type OnboardingV1ClaimedDraft,
} from "~/features/start/state/onboarding-v1-account-data-source";
import { useOnboardingV1FundingController } from "~/features/start/state/use-onboarding-v1-funding-controller";
import {
  useOnboardingV1Recommendations,
  type OnboardingV1RecommendationDataSource,
} from "~/features/start/state/use-onboarding-v1-recommendations";
import type { OnboardingV1AccountDataSource } from "~/features/start/state/onboarding-v1-account-data-source";
import { formatSignedPercent } from "~/lib/display-format";
import { getPublicAssetUrl } from "~/lib/env";
import { m as messages } from "~/lib/i18n";
import { OnboardingV1RetryState } from "./onboarding-v1-retry-state";

interface OnboardingV1FundStepProps {
  readonly allowAutomaticCreate: boolean;
  readonly dataSource: OnboardingV1AccountDataSource;
  readonly draft: OnboardingV1ClaimedDraft;
  readonly onCompleted: () => void;
  readonly onCreatingChange: (isCreating: boolean) => void;
  readonly onDraftChange: (draft: OnboardingV1ClaimedDraft) => void;
  readonly onStarterInvalid: () => Promise<void>;
  readonly recommendationDataSource?: OnboardingV1RecommendationDataSource;
}

export function OnboardingV1FundStep(props: OnboardingV1FundStepProps) {
  const recoveryStartedRef = useRef(false);
  const [starterRecoveryFailed, setStarterRecoveryFailed] = useState(false);
  const recommendations = useOnboardingV1Recommendations({
    answers: {
      interests: props.draft.interests,
      interestsUnknown: props.draft.interestsUnknown,
      selfDescriptions: props.draft.selfDescriptions,
    },
    dataSource: props.recommendationDataSource,
  });
  const selectedRecommendation = recommendations.cards.find(
    (card) => card.blueprintId === props.draft.selectedTemplateId,
  );
  const hasProvisioningState = hasOnboardingV1ProvisioningState(props.draft);
  const selectedRecommendationMissing =
    !hasProvisioningState &&
    !recommendations.isLoading &&
    !recommendations.isError &&
    selectedRecommendation === undefined;
  const controller = useOnboardingV1FundingController({
    active:
      hasProvisioningState ||
      (selectedRecommendation !== undefined && !recommendations.isError),
    allowAutomaticCreate: props.allowAutomaticCreate,
    dataSource: props.dataSource,
    draft: props.draft,
    onCompleted: props.onCompleted,
    onDraftChange: props.onDraftChange,
    recommendationDataSource: props.recommendationDataSource,
  });
  useEffect(() => {
    props.onCreatingChange(controller.isCreating);
    return () => props.onCreatingChange(false);
  }, [controller.isCreating, props.onCreatingChange]);
  const recoverInvalidStarter = useCallback(async () => {
    if (recoveryStartedRef.current) {
      return;
    }
    recoveryStartedRef.current = true;
    setStarterRecoveryFailed(false);
    try {
      await props.onStarterInvalid();
    } catch {
      recoveryStartedRef.current = false;
      setStarterRecoveryFailed(true);
    }
  }, [props.onStarterInvalid]);
  useEffect(() => {
    if (selectedRecommendationMissing) {
      void recoverInvalidStarter();
    }
  }, [recoverInvalidStarter, selectedRecommendationMissing]);
  const portfolioName =
    selectedRecommendation?.blueprintName ??
    props.draft.strategyData?.entry.name ??
    messages.onboarding_v1_pick_untitled_portfolio();
  const recommendationReturn = selectedRecommendation
    ? getOnboardingV1RecommendationReturnDisplay(selectedRecommendation)
    : null;
  const formattedRecommendationReturn =
    recommendationReturn && recommendationReturn.returnPct !== null
      ? formatSignedPercent(recommendationReturn.returnPct, {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : null;
  // Once a portfolio exists, recommendation data is cosmetic and must never
  // block funding an already-created vault.
  const hasRecommendationError =
    props.draft.createdPortfolioId === null && recommendations.isError;
  const hasBlockingRecommendationLoad =
    props.draft.createdPortfolioId === null && recommendations.isLoading;
  const showRetry = controller.operationError || hasRecommendationError;

  return (
    <div
      className="onboarding-v1__fund-step"
      data-testid="onboarding-v1-step-fund"
    >
      <div className="onboarding-v1__fund-heading">
        <h1>{messages.onboarding_v1_fund_heading()}</h1>
        <p>{messages.onboarding_v1_fund_description()}</p>
      </div>

      <div className="onboarding-v1__fund-panel">
        <div className="onboarding-v1__fund-portfolio">
          {selectedRecommendation ? (
            <img
              alt=""
              aria-hidden="true"
              className="onboarding-v1__fund-portfolio-image"
              src={getPublicAssetUrl(selectedRecommendation.heroImagePath)}
            />
          ) : (
            <span
              aria-hidden="true"
              className="onboarding-v1__fund-portfolio-image onboarding-v1__fund-portfolio-image--loading"
            />
          )}
          <div className="onboarding-v1__fund-portfolio-copy">
            <span className="onboarding-v1__fund-portfolio-badge">
              {messages.onboarding_v1_fund_portfolio_badge()}
            </span>
            <strong>{portfolioName}</strong>
            {recommendationReturn ? (
              <span className="onboarding-v1__fund-portfolio-return">
                {recommendationReturn.period === "one_year" &&
                formattedRecommendationReturn
                  ? messages.onboarding_v1_fund_return({
                      value: formattedRecommendationReturn,
                    })
                  : recommendationReturn.period === "all_time" &&
                      formattedRecommendationReturn
                    ? messages.onboarding_v1_fund_all_time_return({
                        value: formattedRecommendationReturn,
                      })
                    : messages.onboarding_flow_not_available_label()}
              </span>
            ) : null}
          </div>
          {selectedRecommendation?.tokens.length ? (
            <StackedTokenIcons
              size={28}
              tokens={selectedRecommendation.tokens}
            />
          ) : null}
        </div>

        <div className="onboarding-v1__fund-action">
          {controller.isCreating ? (
            <p aria-live="polite" role="status">
              {messages.onboarding_v1_fund_creating()}
            </p>
          ) : null}

          {!controller.isCreating && (showRetry || starterRecoveryFailed) ? (
            <OnboardingV1RetryState
              className="onboarding-v1__fund-error"
              onRetry={
                starterRecoveryFailed
                  ? () => void recoverInvalidStarter()
                  : hasRecommendationError && !controller.operationError
                    ? recommendations.retry
                    : controller.retry
              }
            />
          ) : null}

          {!controller.isCreating &&
          !showRetry &&
          !starterRecoveryFailed &&
          !hasBlockingRecommendationLoad &&
          !selectedRecommendationMissing ? (
            <>
              <label htmlFor="onboarding-v1-fund-asset">
                {messages.onboarding_v1_fund_asset_label()}
              </label>
              <NativeSelect
                disabled={
                  controller.isDepositPending || controller.isLoadingAssets
                }
                id="onboarding-v1-fund-asset"
                onChange={(event) =>
                  controller.setSelectedAssetId(event.target.value)
                }
                value={controller.selectedAsset?.assetId ?? ""}
              >
                {controller.fundingAssets.map((asset) => (
                  <NativeSelectOption key={asset.assetId} value={asset.assetId}>
                    {asset.symbol}
                  </NativeSelectOption>
                ))}
              </NativeSelect>

              <label htmlFor="onboarding-v1-fund-amount">
                {messages.onboarding_v1_fund_amount_label()}
              </label>
              <Input
                autoComplete="off"
                disabled={controller.isDepositPending}
                id="onboarding-v1-fund-amount"
                inputMode="decimal"
                onChange={(event) =>
                  controller.setDepositAmount(event.currentTarget.value)
                }
                placeholder="0.0"
                value={controller.depositAmount}
              />

              <Button
                className="onboarding-v1__fund-submit"
                disabled={!controller.canSubmit}
                onClick={() => void controller.submitDeposit()}
                type="button"
                variant="inverse"
              >
                {controller.isDepositPending
                  ? messages.onboarding_v1_fund_confirming()
                  : messages.onboarding_v1_fund_submit()}
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
